import React, { useState, useEffect, useRef, memo, useCallback } from 'react';
import './App.css';
import 'animate.css/animate.min.css';

// Security utility for sanitizing content - in production would use a proper library like DOMPurify
const sanitizeHtml = (html) => {
  // This is a simple implementation - in production use a proper sanitizer library
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  // Remove potentially dangerous elements and attributes
  const scripts = tempDiv.querySelectorAll('script, iframe, object, embed');
  scripts.forEach(script => script.remove());
  
  // Remove dangerous attributes like onclick, onerror, etc.
  const allElements = tempDiv.querySelectorAll('*');
  allElements.forEach(el => {
    Array.from(el.attributes).forEach(attr => {
      // eslint-disable-next-line no-script-url
      if (attr.name.startsWith('on') || (attr.name === 'href' && attr.value.startsWith('javascript:'))) {
        el.removeAttribute(attr.name);
      }
    });
  });
  
  return tempDiv.innerHTML;
};

// Custom hook for scroll animations with enhanced features and security optimizations
const useIntersectionObserver = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const hasAnimated = useRef(false);
  const [animationClass, setAnimationClass] = useState("");
  
  // Using useCallback to avoid recreating observer on each render
  const observer = useRef(null);

  useEffect(() => {
    observer.current = new IntersectionObserver(([entry]) => {
      // Only trigger animation if it hasn't been triggered yet
      if (entry.isIntersecting && !hasAnimated.current) {
        setIsVisible(true);
        hasAnimated.current = true;
      }
    }, {
      threshold: 0.1,
      ...options
    });

    const currentRef = ref.current;
    if (currentRef) {
      observer.current.observe(currentRef);
    }

    return () => {
      if (currentRef && observer.current) {
        observer.current.unobserve(currentRef);
        observer.current.disconnect();
      }
    };
  }, [options]);

  return [ref, isVisible, animationClass, setAnimationClass];
};

// Enhanced Neural Network background with security elements
// Using memo to prevent unnecessary re-renders
const NeuralNetworkBackground = memo(() => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const particlesRef = useRef([]);
  const securityNodesRef = useRef([]);
  const mousePosition = useRef({ x: null, y: null });
  const dataFlowsRef = useRef([]);
  const securityIconsRef = useRef([]);
  
  // Track mouse position for interactive particles
  useEffect(() => {
    const handleMouseMove = (e) => {
      mousePosition.current = {
        x: e.clientX,
        y: e.clientY
      };
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initAnimation();
    };
    
    const createSecurityNode = (x, y, type = 'secure') => {
      return {
        x,
        y,
        radius: 3, // Security nodes are slightly larger
        type, // 'secure' (green) or 'alert' (red)
        pulseDirection: 1,
        pulseSpeed: 0.02,
        originalRadius: 3,
        lastPulseTime: Date.now(),
        pulseDuration: 2000 + Math.random() * 3000 // Random pulse timing
      };
    };
    
    const createSecurityIcon = (type) => {
      const x = Math.random() * canvas.width;
      const y = Math.random() * canvas.height;
      const size = 10 + Math.random() * 5;
      
      return {
        x,
        y,
        size,
        type, // 'shield', 'lock', 'key'
        speedX: (Math.random() - 0.5) * 0.2,
        speedY: (Math.random() - 0.5) * 0.2,
        rotation: 0,
        rotationSpeed: (Math.random() - 0.5) * 0.01
      };
    };
    
    const initAnimation = () => {
      // Optimize particle count based on screen size
      const isMobile = window.innerWidth < 768;
      const particleCount = isMobile ? 
        Math.min(30, Math.floor(window.innerWidth / 40)) : 
        Math.min(80, Math.floor(window.innerWidth / 30));
      
      // Initialize regular nodes (particles)
      particlesRef.current = [];
      for (let i = 0; i < particleCount; i++) {
        particlesRef.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          radius: Math.random() * 1.5 + 0.5, // Smaller neural network nodes
          color: `rgba(${Math.floor(59 + Math.random() * 30)}, ${Math.floor(130 + Math.random() * 40)}, ${Math.floor(246 + Math.random() * 10)}, ${0.5 + Math.random() * 0.3})`,
          speedX: (Math.random() - 0.5) * 0.1, // Reduced speed by 3x
          speedY: (Math.random() - 0.5) * 0.1, // Reduced speed by 3x
          // Add a slight pulsing effect
          pulseDirection: Math.random() > 0.5 ? 1 : -1,
          pulseSpeed: Math.random() * 0.005 + 0.002, // Slower pulse
          originalRadius: Math.random() * 1.5 + 0.5,
          // Neuron state
          active: false,
          lastActivationTime: 0,
          activationDuration: 500 + Math.random() * 1000
        });
      }
      
      // Initialize security nodes (fewer, special colored nodes)
      securityNodesRef.current = [];
      const securityNodeCount = Math.floor(particleCount * 0.15); // 15% of regular nodes
      
      for (let i = 0; i < securityNodeCount; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const type = Math.random() > 0.7 ? 'alert' : 'secure'; // 30% alert nodes, 70% secure nodes
        
        securityNodesRef.current.push(createSecurityNode(x, y, type));
      }
      
      // Initialize data flows (animated connections between nodes)
      dataFlowsRef.current = [];
      
      // Initialize security icons (shields, locks, keys)
      securityIconsRef.current = [];
      const iconCount = Math.floor(particleCount * 0.05); // 5% of regular nodes
      
      const iconTypes = ['shield', 'lock', 'key'];
      for (let i = 0; i < iconCount; i++) {
        const type = iconTypes[Math.floor(Math.random() * iconTypes.length)];
        securityIconsRef.current.push(createSecurityIcon(type));
      }
    };
    
    const drawSecurityIcon = (icon) => {
      ctx.save();
      ctx.translate(icon.x, icon.y);
      ctx.rotate(icon.rotation);
      
      // Draw different security icons based on type
      if (icon.type === 'shield') {
        // Shield
        const size = icon.size;
        ctx.fillStyle = 'rgba(34, 197, 94, 0.6)'; // Green shield
        
        // Draw shield shape
        ctx.beginPath();
        ctx.moveTo(0, -size);
        ctx.lineTo(size, -size/2);
        ctx.lineTo(size, size/2);
        ctx.lineTo(0, size);
        ctx.lineTo(-size, size/2);
        ctx.lineTo(-size, -size/2);
        ctx.closePath();
        ctx.fill();
        
        // Draw checkmark inside
        ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(-size/3, 0);
        ctx.lineTo(-size/8, size/3);
        ctx.lineTo(size/3, -size/3);
        ctx.stroke();
      } else if (icon.type === 'lock') {
        // Lock
        const size = icon.size;
        ctx.fillStyle = 'rgba(59, 130, 246, 0.6)'; // Blue lock
        
        // Lock body
        ctx.beginPath();
        ctx.rect(-size/2, -size/4, size, size);
        ctx.fill();
        
        // Lock shackle
        ctx.strokeStyle = 'rgba(59, 130, 246, 0.6)';
        ctx.lineWidth = size/3;
        ctx.beginPath();
        ctx.arc(0, -size/4, size/2, Math.PI, 2 * Math.PI);
        ctx.stroke();
      } else if (icon.type === 'key') {
        // Key
        const size = icon.size;
        ctx.fillStyle = 'rgba(245, 158, 11, 0.6)'; // Yellow/gold key
        
        // Key handle (circle)
        ctx.beginPath();
        ctx.arc(0, -size/2, size/2, 0, 2 * Math.PI);
        ctx.fill();
        
        // Key stem
        ctx.fillRect(-size/6, 0, size/3, size);
        
        // Key teeth
        ctx.fillRect(size/6, size/2, size/3, size/6);
        ctx.fillRect(size/6, size*3/4, size/3, size/6);
      }
      
      ctx.restore();
    };
    
    const createDataFlow = (source, target, type = 'normal') => {
      return {
        source,
        target,
        progress: 0,
        speed: 0.003 + Math.random() * 0.008, // Significantly slower data flow
        type, // 'normal', 'secure', or 'alert'
        active: true,
        width: Math.random() * 1.5 + 0.5,
        color: type === 'secure' ? 'rgba(34, 197, 94, 0.7)' : 
               type === 'alert' ? 'rgba(239, 68, 68, 0.7)' : 
               'rgba(59, 130, 246, 0.7)'
      };
    };
    
    const activateNeuron = (index) => {
      const particle = particlesRef.current[index];
      if (!particle.active) {
        particle.active = true;
        particle.lastActivationTime = Date.now();
        
        // Create data flows from this neuron to others
        const flowCount = Math.floor(Math.random() * 3) + 1; // 1-3 connections
        
        for (let i = 0; i < flowCount; i++) {
          // Pick a random target neuron
          const targetIndex = Math.floor(Math.random() * particlesRef.current.length);
          if (targetIndex !== index) {
            // Occasionally create a flow to a security node instead
            if (Math.random() < 0.2 && securityNodesRef.current.length > 0) {
              const securityIndex = Math.floor(Math.random() * securityNodesRef.current.length);
              const type = securityNodesRef.current[securityIndex].type;
              
              dataFlowsRef.current.push(createDataFlow(
                particlesRef.current[index],
                securityNodesRef.current[securityIndex],
                type
              ));
            } else {
              dataFlowsRef.current.push(createDataFlow(
                particlesRef.current[index],
                particlesRef.current[targetIndex],
                'normal'
              ));
            }
          }
        }
      }
    };
    
    const drawParticles = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      // Randomly activate neurons (less frequently)
      if (Math.random() < 0.008 && particlesRef.current.length > 0) {
        activateNeuron(Math.floor(Math.random() * particlesRef.current.length));
      }
      
      // Update and draw data flows
      for (let i = dataFlowsRef.current.length - 1; i >= 0; i--) {
        const flow = dataFlowsRef.current[i];
        
        // Update flow progress
        flow.progress += flow.speed;
        
        if (flow.progress >= 1) {
          // Remove completed flow
          dataFlowsRef.current.splice(i, 1);
          
          // Activate the target neuron if it's a regular particle
          const targetIndex = particlesRef.current.indexOf(flow.target);
          if (targetIndex >= 0) {
            activateNeuron(targetIndex);
          }
        } else {
          // Draw flow
          const startX = flow.source.x;
          const startY = flow.source.y;
          const endX = flow.target.x;
          const endY = flow.target.y;
          
          // Calculate current position along the line
          const currentX = startX + (endX - startX) * flow.progress;
          const currentY = startY + (endY - startY) * flow.progress;
          
          // Draw data packet
          ctx.beginPath();
          ctx.arc(currentX, currentY, flow.width * 2, 0, Math.PI * 2);
          ctx.fillStyle = flow.color;
          ctx.fill();
          
          // Draw flow trail
          ctx.beginPath();
          ctx.moveTo(startX, startY);
          ctx.lineTo(currentX, currentY);
          ctx.strokeStyle = flow.color;
          ctx.lineWidth = flow.width;
          ctx.stroke();
          
          // Draw dashed line for remaining path
          ctx.beginPath();
          ctx.setLineDash([2, 4]);
          ctx.moveTo(currentX, currentY);
          ctx.lineTo(endX, endY);
          ctx.strokeStyle = flow.color.replace('0.7', '0.3');
          ctx.lineWidth = flow.width * 0.8;
          ctx.stroke();
          ctx.setLineDash([]);
        }
      }
      
      // Update and draw regular particles (neurons)
      particlesRef.current.forEach(particle => {
        // Check if neural activation should end
        if (particle.active && Date.now() - particle.lastActivationTime > particle.activationDuration) {
          particle.active = false;
        }
        
        // Pulse the particle size
        particle.radius += particle.pulseSpeed * particle.pulseDirection;
        if (particle.radius > particle.originalRadius * 1.5 || particle.radius < particle.originalRadius * 0.5) {
          particle.pulseDirection *= -1;
        }
        
        // Draw particle
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        
        // Use different color if neuron is active
        if (particle.active) {
          // Enhanced glow for active neurons
          ctx.shadowColor = 'rgba(59, 130, 246, 0.8)';
          ctx.shadowBlur = 10;
          ctx.fillStyle = 'rgba(59, 130, 246, 0.8)';
        } else {
          ctx.shadowBlur = 0;
          ctx.fillStyle = particle.color;
        }
        
        ctx.fill();
        
        // Update position
        particle.x += particle.speedX;
        particle.y += particle.speedY;
        
        // Boundary check with wrap-around
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;
        
        // Add subtle attraction toward mouse position when mouse is on screen
        if (mousePosition.current.x !== null && mousePosition.current.y !== null) {
          const dx = mousePosition.current.x - particle.x;
          const dy = mousePosition.current.y - particle.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 200) {
            const force = 0.2 / distance;
            particle.speedX += dx * force * 0.05;
            particle.speedY += dy * force * 0.05;
            
            // Add some friction to prevent excessive speeds
            particle.speedX *= 0.98;
            particle.speedY *= 0.98;
            
            // Create data flow from mouse interaction occasionally (reduced frequency)
            if (particle.active && Math.random() < 0.005) {
              // Find a target
              if (securityNodesRef.current.length > 0 && Math.random() < 0.3) {
                // Flow to security node sometimes
                const securityIndex = Math.floor(Math.random() * securityNodesRef.current.length);
                const type = securityNodesRef.current[securityIndex].type;
                
                dataFlowsRef.current.push(createDataFlow(
                  particle,
                  securityNodesRef.current[securityIndex],
                  type
                ));
              } else {
                // Flow to another regular neuron
                const targetIndex = Math.floor(Math.random() * particlesRef.current.length);
                if (particlesRef.current[targetIndex] !== particle) {
                  dataFlowsRef.current.push(createDataFlow(
                    particle,
                    particlesRef.current[targetIndex],
                    'normal'
                  ));
                }
              }
            }
          }
        }
      });
      
      // Update and draw security nodes
      securityNodesRef.current.forEach(node => {
        // Pulse the node
        const currentTime = Date.now();
        if (currentTime - node.lastPulseTime > node.pulseDuration) {
          node.pulseDirection *= -1;
          node.lastPulseTime = currentTime;
        }
        
        node.radius += node.pulseSpeed * node.pulseDirection;
        if (node.radius > node.originalRadius * 2 || node.radius < node.originalRadius * 0.7) {
          node.pulseDirection *= -1;
        }
        
        // Draw security node
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        
        // Use color based on type
        if (node.type === 'secure') {
          ctx.shadowColor = 'rgba(34, 197, 94, 0.8)';
          ctx.fillStyle = 'rgba(34, 197, 94, 0.8)';
        } else { // alert
          ctx.shadowColor = 'rgba(239, 68, 68, 0.8)';
          ctx.fillStyle = 'rgba(239, 68, 68, 0.8)';
        }
        
        ctx.shadowBlur = 10;
        ctx.fill();
        ctx.shadowBlur = 0;
      });
      
      // Update and draw security icons
      securityIconsRef.current.forEach(icon => {
        // Update position
        icon.x += icon.speedX;
        icon.y += icon.speedY;
        
        // Update rotation
        icon.rotation += icon.rotationSpeed;
        
        // Boundary check with wrap-around
        if (icon.x < -icon.size) icon.x = canvas.width + icon.size;
        if (icon.x > canvas.width + icon.size) icon.x = -icon.size;
        if (icon.y < -icon.size) icon.y = canvas.height + icon.size;
        if (icon.y > canvas.height + icon.size) icon.y = -icon.size;
        
        // Draw security icon
        drawSecurityIcon(icon);
      });
      
      // Connect nearby particles to form neural network connections
      const connectionDistance = 120;
      const particles = particlesRef.current;
      
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          
          // Use distance squared comparison for performance
          const distSquared = dx * dx + dy * dy;
          const maxDistSquared = connectionDistance * connectionDistance;
          
          if (distSquared < maxDistSquared) {
            // Calculate base opacity
            let opacity = 0.15 * (1 - Math.sqrt(distSquared) / connectionDistance);
            
            // Make connections brighter between active neurons
            if (particles[i].active && particles[j].active) {
              opacity *= 3;
              
              // Draw enhanced connection for active neurons
              ctx.beginPath();
              ctx.strokeStyle = `rgba(59, 130, 246, ${opacity * 1.5})`;
              ctx.lineWidth = 1.5;
              ctx.setLineDash([5, 5]);
              ctx.moveTo(particles[i].x, particles[i].y);
              ctx.lineTo(particles[j].x, particles[j].y);
              ctx.stroke();
              ctx.setLineDash([]);
            } else {
              // Standard connection
              ctx.beginPath();
              ctx.strokeStyle = `rgba(59, 130, 246, ${opacity})`;
              ctx.lineWidth = 0.5;
              ctx.moveTo(particles[i].x, particles[i].y);
              ctx.lineTo(particles[j].x, particles[j].y);
              ctx.stroke();
            }
            
            // Increase opacity when close to mouse
            if (mousePosition.current.x !== null && mousePosition.current.y !== null) {
              const mouseDx = (particles[i].x + particles[j].x) / 2 - mousePosition.current.x;
              const mouseDy = (particles[i].y + particles[j].y) / 2 - mousePosition.current.y;
              const mouseDistSquared = mouseDx * mouseDx + mouseDy * mouseDy;
              
              if (mouseDistSquared < 40000) { // 200^2
                // Activate neurons near mouse occasionally (reduced frequency)
                if (Math.random() < 0.003) {
                  if (!particles[i].active) {
                    particles[i].active = true;
                    particles[i].lastActivationTime = Date.now();
                  }
                  if (!particles[j].active) {
                    particles[j].active = true;
                    particles[j].lastActivationTime = Date.now();
                  }
                }
              }
            }
          }
        }
      }
      
      // Connect security nodes to nearby particles
      for (let i = 0; i < securityNodesRef.current.length; i++) {
        const securityNode = securityNodesRef.current[i];
        
        for (let j = 0; j < particles.length; j++) {
          const particle = particles[j];
          const dx = securityNode.x - particle.x;
          const dy = securityNode.y - particle.y;
          
          // Use distance squared comparison for performance
          const distSquared = dx * dx + dy * dy;
          const securityConnectionDistance = 150; // Slightly larger than normal connections
          const maxDistSquared = securityConnectionDistance * securityConnectionDistance;
          
          if (distSquared < maxDistSquared) {
            // Calculate base opacity
            let opacity = 0.2 * (1 - Math.sqrt(distSquared) / securityConnectionDistance);
            
            // Enhanced connection if particle is active
            if (particle.active) {
              opacity *= 2;
              
              // Determine connection style based on security node type
              if (securityNode.type === 'secure') {
                ctx.strokeStyle = `rgba(34, 197, 94, ${opacity})`;
              } else { // alert
                ctx.strokeStyle = `rgba(239, 68, 68, ${opacity})`;
              }
              
              ctx.beginPath();
              ctx.lineWidth = 1;
              ctx.setLineDash([3, 3]);
              ctx.moveTo(securityNode.x, securityNode.y);
              ctx.lineTo(particle.x, particle.y);
              ctx.stroke();
              ctx.setLineDash([]);
            }
          }
        }
      }
      
      animationRef.current = requestAnimationFrame(drawParticles);
    };
    
    window.addEventListener('resize', resizeCanvas);
    resizeCanvas();
    drawParticles();
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationRef.current);
    };
  }, []);
  
  return <canvas ref={canvasRef} className="particle-canvas" />;
});

// Enhanced Loading Screen with Neural Network theme
const NeuralLoadingScreen = memo(({ progress }) => {
  return (
    <div className="loading-screen">
      <div className="loader-container">
        <div className="loader">
          <div className="neural-loader">
            {[0, 1, 2].map(row => (
              <div key={row} className="neural-row">
                {[0, 1, 2].map(col => (
                  <div 
                    key={col} 
                    className={`neural-node ${
                      row === 1 && col === 1 ? 'pulse' : 
                      row === 0 && col === 2 ? 'secure' : 
                      row === 2 && col === 0 ? 'alert' : ''
                    }`}
                    style={{ 
                      animationDelay: `${row * 0.2 + col * 0.1}s` 
                    }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
        <div className="loader-progress">
          <div 
            className="progress-bar" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>
      <p className="loading-text">
        <span className="loading-word">Initializing Neural Network</span>
        <span className="loading-dots">
          <span className="dot">.</span>
          <span className="dot">.</span>
          <span className="dot">.</span>
        </span>
      </p>
    </div>
  );
});

// Animated typing effect with cursor
const TypedText = memo(({ text, speed = 100, onComplete = () => {} }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [isComplete, setIsComplete] = useState(false);
  
  useEffect(() => {
    if (displayedText.length < text.length) {
      const timeout = setTimeout(() => {
        setDisplayedText(text.slice(0, displayedText.length + 1));
      }, speed);
      
      return () => clearTimeout(timeout);
    } else if (!isComplete) {
      setIsComplete(true);
      onComplete();
    }
  }, [displayedText, text, speed, isComplete, onComplete]);
  
  return (
    <span className="typed-text-wrapper">
      {displayedText}<span className={`cursor ${isComplete ? "blink" : ""}`}>|</span>
    </span>
  );
});

// Animated role switcher with smooth transitions
const RoleSwitcher = memo(({ roles }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [displayedRole, setDisplayedRole] = useState(roles[0]);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      
      // First fade out the current role
      setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % roles.length);
        setDisplayedRole(roles[(currentIndex + 1) % roles.length]);
        
        // Then fade in the new role
        setTimeout(() => {
          setIsAnimating(false);
        }, 300);
      }, 500);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [currentIndex, roles]);
  
  return (
    <div className="role-switcher">
      <p className={`role ${isAnimating ? "fade-out" : "fade-in"}`}>{displayedRole}</p>
    </div>
  );
});

const Hero = () => {
  const [typingComplete, setTypingComplete] = useState(false);
  const roles = ["AI Enthusiast", "Security Engineer", "Self-Hosting Advocate"];
  
  return (
    <section className="hero">
      <div className="hero-content animate__animated animate__fadeInDown">
        <h1 className="gradient-text">Abhishek Sreenivasa</h1>
        <h2 className="specialty">
          <TypedText 
            text="Identity & Access Management Specialist" 
            onComplete={() => setTypingComplete(true)}
          />
        </h2>
        {typingComplete && <RoleSwitcher roles={roles} />}
        <a href="#about" className="scroll-down">
          Discover More <span className="icon-bounce">↓</span>
        </a>
      </div>
    </section>
  );
};

// Enhanced About section with staggered text reveal
const About = () => {
  const [ref, isVisible] = useIntersectionObserver();
  const [textRef1, isTextVisible1] = useIntersectionObserver({ threshold: 0.5 });
  const [textRef2, isTextVisible2] = useIntersectionObserver({ threshold: 0.5 });

  return (
    <section id="about" className="about section">
      <div 
        ref={ref} 
        className={`content-wrapper ${isVisible ? "animate__animated animate__fadeInUp visible" : ""}`}
        style={{ willChange: isVisible ? 'transform, opacity' : 'auto' }}
      >
        <h2 className="section-title">About Me</h2>
        <p 
          ref={textRef1}
          className={`about-text ${isTextVisible1 ? "text-reveal" : ""}`}
        >
          I'm an Identity & Access Management specialist with expertise in Azure, ForgeRock, Okta, FIM, MIM, and CyberArk. 
          Recently, I've developed a strong passion for Artificial Intelligence and how it's shaping our world, which has inspired me to explore emerging AI technologies and learn about their potential; including applications in cybersecurity.
        </p>
        <p 
          ref={textRef2}
          className={`about-text ${isTextVisible2 ? "text-reveal" : ""}`}
          style={{ transitionDelay: "0.2s" }}
        >
          Beyond my professional work, I'm deeply interested in ethical AI development, self-hosting services, and data privacy. 
          I've started personally learning and implementing self-hosting practices to take control of my data, while also exploring innovative AI tools  
          that focus on ethical standards.
        </p>
      </div>
    </section>
  );
};

// Enhanced Project Card with interactive elements
const ProjectCard = ({ project, index }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      ref={ref}
      className={`project-card ${isVisible ? "animate__animated animate__fadeInUp visible" : ""} ${isHovered ? "hovered" : ""}`}
      style={{ 
        animationDelay: `${index * 0.15}s`,
        willChange: isVisible ? 'transform, opacity' : 'auto'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="card-content">
        <div className="card-icon">
          {project.icon}
        </div>
        <h3>{project.title}</h3>
        <p>{project.description}</p>
        <div className="project-tags">
          {project.tags.map((tag, tagIndex) => (
            <span 
              key={tagIndex} 
              className="project-tag"
              style={{ transitionDelay: `${tagIndex * 0.05}s` }}
            >
              {tag}
            </span>
          ))}
        </div>
      </div>
      <div className="card-background"></div>
    </div>
  );
};

// Projects Section with enhanced animations
const Projects = () => {
  const projects = [
    {
      title: "Self-Hosted AI Assistant",
      description: "Self-hosted language model assistant that respects privacy while providing helpful responses for daily tasks and research.",
      tags: ["LLM", "AI", "SelfHosted", "Privacy", "Self-Hosted"],
      icon: <span className="emoji-icon">🤖</span>
    },
    {
      title: "Secure Data Management",
      description: "Exploring encryption and secure storage solutions for protecting sensitive data used in AI training and inference.",
      tags: ["Encryption", "Privacy", "Data Security", "ZeroTrust"],
      icon: <span className="emoji-icon">🔒</span>
    },
    {
      title: "Smart Home Automation",
      description: "Privacy-focused home automation using local AI models instead of cloud-based solutions.",
      tags: ["IoT", "Privacy", "HomeAutomation"],
      icon: <span className="emoji-icon">🏠</span>
    }
  ];

  const [sectionRef, isSectionVisible] = useIntersectionObserver({ threshold: 0.1 });

  return (
    <section id="projects" className="projects section" ref={sectionRef}>
      <h2 className={`section-title ${isSectionVisible ? "title-animation" : ""}`}>
        Focus Areas
      </h2>
      <div className="projects-container">
        {projects.map((project, index) => (
          <ProjectCard key={index} project={project} index={index} />
        ))}
      </div>
    </section>
  );
};

// Enhanced Skill item with animation
const SkillItem = ({ skill, delay, isVisible, category }) => {
  // Check if this skill should have a security theme
  const securityKeywords = ['Zero Trust', 'Security', 'Privacy', 'Encryption', 'RBAC', 'MFA', 'CyberArk'];
  const isSecurityRelated = securityKeywords.some(keyword => 
    skill.toLowerCase().includes(keyword.toLowerCase())
  );
  
  return (
    <li 
      className={`skill-item ${isSecurityRelated ? 'security' : ''} ${isVisible ? "skill-appear" : ""}`}
      style={{ transitionDelay: `${delay}s` }}
    >
      {skill}
    </li>
  );
};

// Separate component for each skill category with more animations
const SkillCategory = ({ category, index }) => {
  const [ref, isVisible] = useIntersectionObserver();
  
  return (
    <div 
      ref={ref} 
      className={`skill-category ${isVisible ? "animate__animated animate__fadeInUp visible" : ""}`}
      style={{ 
        animationDelay: `${index * 0.15}s`,
        willChange: isVisible ? 'transform, opacity' : 'auto'
      }}
    >
      <div className="category-header">
        <h3>{category.name}</h3>
        <div className={`underline ${isVisible ? "grow" : ""}`}></div>
      </div>
      <ul className="skills-list">
        {category.skills.map((skill, i) => (
          <SkillItem 
            key={i}
            skill={skill} 
            delay={(index * 0.05) + (i * 0.05)}
            isVisible={isVisible}
            category={category.name}
          />
        ))}
      </ul>
    </div>
  );
};

const Skills = () => {
  const skillCategories = [
    {
      name: "Identity & Access Management",
      skills: ["Azure Entra ID", "ForgeRock", "Okta Administration", "CyberArk", "FIM & MIM", "ADFS & SSO", "MFA, RBAC & SAML" , "Zero Trust"]
    },
    {
      name: "AI & Data Privacy",
      skills: ["LLM Applications", "Self-Hosting", "Data Security", "Privacy Engineering", "Emerging AI Technologies", "Ethical AI Practices"]
    },
    {
      name: "Technologies",
      skills: ["Azure DevOps", "API Management", "Postman", "Splunk", "Linux", "CI/CD Pipelines"]
    }
  ];
  
  const [sectionRef, isSectionVisible] = useIntersectionObserver({ threshold: 0.1 });

  return (
    <section id="skills" className="skills section" ref={sectionRef}>
      <h2 className={`section-title ${isSectionVisible ? "title-animation" : ""}`}>
        Skills & Interests
      </h2>
      
      <div className="skills-container">
        {skillCategories.map((category, index) => (
          <SkillCategory key={index} category={category} index={index} />
        ))}
      </div>
    </section>
  );
};

// New Blog Components

// Blog posts utility and secure data fetching
const useBlogPosts = () => {
  // In a production environment, you would fetch this from a secure API endpoint
  // For now, we'll use a local constant with sample data
  const [posts, setPosts] = useState([
    {
      id: 2,
      title: "Self-Hosting AI Services: A Practical Guide",
      date: "February 15, 2025",
      summary: "How to set up your own AI services at home while maintaining control over your data and privacy.",
      content: `<p>After exploring various AI tools and services, I've become increasingly concerned about data privacy. This led me to experiment with self-hosting AI services, which gives me the benefits of AI while maintaining control over my data.</p>
      
      <h3>Why Self-Host AI?</h3>
      <p>There are several compelling reasons to consider self-hosting:</p>
      <ul>
        <li>Complete data privacy and control</li>
        <li>No subscription costs for many open-source models</li>
        <li>Customization options not available in commercial services</li>
        <li>No internet required for inference once models are downloaded</li>
      </ul>
      
      <h3>Getting Started with Local LLMs</h3>
      <p>The simplest entry point is running lightweight language models on your own hardware. Here's what you'll need:</p>
      <ul>
        <li>A decent GPU (though some models can run on CPU)</li>
        <li>Software like <a href="#" rel="noopener noreferrer">llama.cpp</a> or <a href="#" rel="noopener noreferrer">oobabooga/text-generation-webui</a></li>
        <li>Quantized models that can run efficiently on consumer hardware</li>
      </ul>
      
      <p>My current setup uses a system with an RTX 3080 running 7B parameter models quantized to 4-bit precision, which provides reasonable performance while maintaining good output quality.</p>
      
      <h3>Setting Up a Private Voice Assistant</h3>
      <p>For those interested in a privacy-focused alternative to commercial voice assistants, I recommend looking into open-source options like:</p>
      <ul>
        <li>Rhasspy for voice recognition</li>
        <li>LocalAI for processing</li>
        <li>Home Assistant for device integration</li>
      </ul>
      
      <p>I've been running this setup for several months now, and while it requires more setup than commercial options, the privacy benefits are significant.</p>`,
      author: "Abhishek Sreenivasa",
      tags: ["AI", "Self-Hosting", "Privacy"]
    }
  ]);

  // Function to add a new post (would integrate with a backend in production)
  const addPost = useCallback((newPost) => {
    // Sanitize the content before storing using our sanitizeHtml function
    const sanitizedPost = {
      ...newPost,
      title: newPost.title ? newPost.title.trim().substring(0, 100) : '', // Limit title length
      summary: newPost.summary ? newPost.summary.trim().substring(0, 250) : '', // Limit summary length
      content: newPost.content ? sanitizeHtml(newPost.content) : '', // Sanitize HTML content
      id: Date.now(), // Simple unique ID generator
      date: new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }),
      // Sanitize and limit tags
      tags: Array.isArray(newPost.tags) ? 
        newPost.tags
          .filter(tag => typeof tag === 'string')
          .map(tag => tag.trim().substring(0, 30))
          .slice(0, 5) : []
    };
    
    setPosts(prevPosts => [sanitizedPost, ...prevPosts]);
  }, []);

  return { posts, addPost };
};

// Blog post listing component
const BlogPostCard = memo(({ post, onClick }) => {
  const [ref, isVisible] = useIntersectionObserver();
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      ref={ref}
      className={`blog-post-card ${isVisible ? "animate__animated animate__fadeInUp visible" : ""} ${isHovered ? "hovered" : ""}`}
      style={{ animationDelay: `${0.1}s` }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={() => onClick(post.id)}
    >
      <div className="post-date">{post.date}</div>
      <h3 className="post-title">{post.title}</h3>
      {post.author && <div className="post-author">By: {post.author}</div>}
      <p className="post-summary">{post.summary}</p>
      <div className="post-tags">
        {post.tags.map((tag, index) => (
          <span key={index} className="post-tag">{tag}</span>
        ))}
      </div>
      <div className="post-footer">
        <span className="read-more">Read More →</span>
      </div>
    </div>
  );
});

// Removed Comment component

// Single blog post view component with enhanced security
const BlogPost = memo(({ post, onBack }) => {
  const [ref, isVisible] = useIntersectionObserver();
  
  if (!post) return null;
  
  return (
    <div 
      ref={ref}
      className={`blog-post ${isVisible ? "animate__animated animate__fadeIn visible" : ""}`}
    >
      <button className="back-button" onClick={onBack}>
        ← Back to posts
      </button>
      
      <article className="post-content">
        <header className="post-header">
          <div className="post-meta">
            <span className="post-date">{post.date}</span>
            {post.author && <span className="post-author">by {post.author}</span>}
          </div>
          <h1 className="post-title">{post.title}</h1>
          <div className="post-tags">
            {post.tags.map((tag, index) => (
              <span key={index} className="post-tag">{tag}</span>
            ))}
          </div>
        </header>
        
        <div 
          className="post-body"
          // Sanitize content again at render time for extra security
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content) }}
        />
        
        <footer className="post-footer">
          <div className="share-section">
            <span>Share this post:</span>
            <div className="social-share">
              <a 
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}`}
                className="share-link" 
                aria-label="Share on X"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="social-icon">x</span>
              </a>
              <a 
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}&title=${encodeURIComponent(post.title)}`}
                className="share-link" 
                aria-label="Share on LinkedIn"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="social-icon">in</span>
              </a>
            </div>
          </div>
        </footer>
      </article>
      
      {/* Comments section removed */}
    </div>
  );
});

// Blog main component with enhanced blogging capabilities
const Blog = memo(() => {
  const { posts } = useBlogPosts();
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [sectionRef, isSectionVisible] = useIntersectionObserver({ threshold: 0.1 });
  
  const selectedPost = posts.find(post => post.id === selectedPostId);
  
  const handlePostClick = (postId) => {
    setSelectedPostId(postId);
    // Instead of scrolling to the very top, scroll to the blog section
    const blogSection = document.getElementById('blog');
    if (blogSection) {
      blogSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  const handleBackClick = () => {
    setSelectedPostId(null);
    // Scroll to the blog section header instead of the very top
    const blogSection = document.getElementById('blog');
    if (blogSection) {
      blogSection.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  return (
    <section id="blog" className="blog section" ref={sectionRef}>
      <h2 className={`section-title ${isSectionVisible ? "title-animation" : ""}`}>
        Blog
      </h2>
      
      {selectedPost ? (
        <BlogPost 
          post={selectedPost} 
          onBack={handleBackClick}
        />
      ) : (
        <div className="blog-posts-container">
          {posts.map(post => (
            <BlogPostCard 
              key={post.id} 
              post={post} 
              onClick={handlePostClick}
            />
          ))}
        </div>
      )}
    </section>
  );
});

// Digital Business Card Component with enhanced security
const DigitalBusinessCard = memo(({ onClose, initAnimation = false }) => {
  const [showCopySuccess, setShowCopySuccess] = useState(false);
  const email = "abhishek@zonvec.com";
  const [animationState, setAnimationState] = useState(0);
  
  useEffect(() => {
    if (initAnimation) {
      // Sequence of animations for card appearance
      const timer1 = setTimeout(() => setAnimationState(1), 100);
      const timer2 = setTimeout(() => setAnimationState(2), 500);
      const timer3 = setTimeout(() => setAnimationState(3), 900);
      
      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
        clearTimeout(timer3);
      };
    }
  }, [initAnimation]);
  
  // Enhanced secure email copying with proper error handling
  const handleCopyEmail = useCallback(() => {
    navigator.clipboard.writeText(email)
      .then(() => {
        setShowCopySuccess(true);
        setTimeout(() => setShowCopySuccess(false), 2000);
      })
      .catch(() => {
        // Handle error without logging sensitive information
        alert('Could not copy email. Please try again or copy it manually.');
      });
  }, [email]);
  
  return (
    <div className="digital-card animate__animated animate__zoomIn">
      <div className={`card-inner ${animationState >= 1 ? 'card-reveal' : ''}`}>
        <div className="card-header">
          <div className="card-logo">
            <span className="logo-text">AS</span>
            <div className="logo-underline"></div>
          </div>
          <h2 className={`card-name ${animationState >= 2 ? 'text-reveal' : ''}`}>Abhishek Sreenivasa</h2>
          <p className={`card-title ${animationState >= 2 ? 'text-reveal-delay' : ''}`}>
            Identity & Access Management Specialist
          </p>
        </div>
        
        <div className={`card-body ${animationState >= 3 ? 'body-reveal' : ''}`}>
          <div className="email-container">
            <div className="email-label">
              <span className="email-icon">✉️</span>
              <span>Email:</span>
            </div>
            <div className="email-value">
              <span className="gradient-text">{email}</span>
              <button 
                className="copy-btn" 
                onClick={handleCopyEmail}
                aria-label="Copy email address"
              >
                {showCopySuccess ? (
                  <span className="copy-success">✓</span>
                ) : (
                  <span className="copy-icon">📋</span>
                )}
              </button>
            </div>
          </div>
          
          <div className="security-note">
            <span className="security-icon">🔒</span>
            <span>End-to-end encrypted communication preferred</span>
          </div>
        </div>
        
        <div className="card-footer">
          <div className="card-socials">
            <a href="https://linkedin.com/in/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn Profile">
              <span className="social-icon">in</span>
            </a>
          </div>
        </div>
      </div>
      
      <div className="card-background"></div>
      <button className="card-close" onClick={onClose} aria-label="Close">×</button>
    </div>
  );
});

// Contact Modal to display the digital business card
const ContactModal = memo(({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <DigitalBusinessCard onClose={onClose} initAnimation={true} />
      </div>
    </div>
  );
});

const ContactBubble = memo(({ onClick }) => {
  return (
    <div className="contact-bubble" onClick={onClick}>
      <div className="bubble-content">
        <span className="contact-icon">✉️</span>
        <span className="secure-shield">🔒</span>
      </div>
      <div className="bubble-rings">
        <div className="ring ring1"></div>
        <div className="ring ring2"></div>
      </div>
    </div>
  );
});

const SocialLinks = memo(() => (
  <div className="social-links">
    <a href="https://linkedin.com/in/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn Profile">
      <span className="social-icon">in</span>
    </a>
  </div>
));

const Footer = memo(() => (
  <footer className="footer">
    <div className="footer-content">
      <p>&copy; 2025 Abhishek Sreenivasa. All rights reserved.</p>
      <SocialLinks />
    </div>
  </footer>
));

const NavBar = memo(() => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  
  useEffect(() => {
    const handleScroll = () => {
      // For navbar background
      const isScrolled = window.scrollY > 50;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
      
      // For active section highlighting
      const sections = ['about', 'projects', 'skills', 'blog'];
      let current = "";
      
      for (let section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 100 && rect.bottom >= 100) {
            current = section;
            break;
          }
        }
      }
      
      if (current !== activeSection) {
        setActiveSection(current);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled, activeSection]);
  
  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <a href="#top" className="nav-logo-link" aria-label="Go to top">
        <div className="nav-logo">
          <span className="logo-text">AS</span>
          <div className="logo-underline"></div>
        </div>
      </a>
      <div className="nav-links">
        <a href="#about" className={activeSection === 'about' ? 'active' : ''}>About</a>
        <a href="#projects" className={activeSection === 'projects' ? 'active' : ''}>Projects</a>
        <a href="#skills" className={activeSection === 'skills' ? 'active' : ''}>Skills</a>
        <a href="#blog" className={activeSection === 'blog' ? 'active' : ''}>Blog</a>
      </div>
    </nav>
  );
});

function App() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const openContactModal = () => {
    setIsContactModalOpen(true);
  };

  const closeContactModal = () => {
    setIsContactModalOpen(false);
  };

  useEffect(() => {
    // Simulate progressive loading for a more engaging load screen
    const loadingInterval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 100) {
          clearInterval(loadingInterval);
          setTimeout(() => setLoading(false), 300);
          return 100;
        }
        return prev + Math.floor(Math.random() * 15) + 5;
      });
    }, 200);
    
    // Enhanced security headers
    // Content Security Policy - restrict resources to improve security
    const metaCSP = document.createElement('meta');
    metaCSP.httpEquiv = 'Content-Security-Policy';
    metaCSP.content = "default-src 'self'; script-src 'self'; style-src 'self' https://fonts.googleapis.com; font-src 'self' https://fonts.gstatic.com; img-src 'self' data:; connect-src 'self' https://8r7p1x5hr6.execute-api.ap-southeast-2.amazonaws.com; form-action 'self'; frame-ancestors 'none'; base-uri 'self'; object-src 'none';";
    document.head.appendChild(metaCSP);
    
    // Prevent clickjacking
    const metaXFrame = document.createElement('meta');
    metaXFrame.httpEquiv = 'X-Frame-Options';
    metaXFrame.content = 'DENY';
    document.head.appendChild(metaXFrame);
    
    // Strict transport security - force HTTPS
    const metaHSTS = document.createElement('meta');
    metaHSTS.httpEquiv = 'Strict-Transport-Security';
    metaHSTS.content = 'max-age=31536000; includeSubDomains; preload';
    document.head.appendChild(metaHSTS);
    
    // Content type options - prevent MIME type sniffing
    const metaContentType = document.createElement('meta');
    metaContentType.httpEquiv = 'X-Content-Type-Options';
    metaContentType.content = 'nosniff';
    document.head.appendChild(metaContentType);
    
    // Referrer policy - limit information leakage
    const metaReferrer = document.createElement('meta');
    metaReferrer.name = 'referrer';
    metaReferrer.content = 'strict-origin-when-cross-origin';
    document.head.appendChild(metaReferrer);
    
    return () => {
      clearInterval(loadingInterval);
      document.head.removeChild(metaCSP);
      document.head.removeChild(metaXFrame);
      document.head.removeChild(metaHSTS);
      document.head.removeChild(metaContentType);
      document.head.removeChild(metaReferrer);
    };
  }, []);

  if (loading) {
    return <NeuralLoadingScreen progress={loadingProgress} />;
  }

  return (
    <div className="App">
      <NeuralNetworkBackground />
      <NavBar />
      <Hero />
      <main>
        <About />
        <Projects />
        <Skills />
        <Blog />
      </main>
      <Footer />
      <ContactBubble onClick={openContactModal} />
      <ContactModal isOpen={isContactModalOpen} onClose={closeContactModal} />
    </div>
  );
}

export default App;